import type { ReactNode } from 'react';

import './page.css';

/** Props for `EmptyState`. */
export interface EmptyStateProps {
  /**
   * A lucide-react icon, shown in a sage squircle tile. Omit it for the
   * brand's × instead (the public shell does, so it needs no icon code).
   */
  icon?: ReactNode;
  title: string;
  /** What this place is for, or why it is empty. */
  children?: ReactNode;
  /** The next step: usually one button or link. */
  actions?: ReactNode;
  /** Heading level for the title (default 2; 1 when it is the whole page). */
  level?: 1 | 2 | 3;
}

/**
 * What a list or page shows when there is nothing yet: what will appear
 * here and the next step, never just "No data".
 *
 * @example
 * <EmptyState icon={<FileText />} title="No references yet" actions={<Button variant="primary">Ask a referee</Button>}>
 *   References you request appear here.
 * </EmptyState>
 */
export function EmptyState({ icon, title, children, actions, level = 2 }: EmptyStateProps) {
  const Heading = `h${String(level)}` as 'h1' | 'h2' | 'h3';
  return (
    <div className="empty">
      <span className="empty__tile" aria-hidden="true">
        {icon ?? <span className="empty__mark">×</span>}
      </span>
      <Heading className="empty__title">{title}</Heading>
      {children ? <div className="empty__text">{children}</div> : null}
      {actions ? <div className="empty__actions">{actions}</div> : null}
    </div>
  );
}
